import { useEffect, useState } from "react";
import { Plus, Loader2, FolderKanban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { SERVICES } from "@/lib/constants";

const STATUSES = ["planning", "scheduled", "in_progress", "on_hold", "completed"];
const label = (s) => s.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(null);
  const [update, setUpdate] = useState("");
  const empty = { customer_name: "", customer_email: "", title: "", service_type: SERVICES[0].title, address: "", status: "planning" };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.customer_name || !form.customer_email || !form.title) { toast.error("Customer name, email and title are required."); return; }
    setSaving(true);
    try { await api.post("/projects", form); toast.success("Project created"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  const patch = async (id, body) => {
    try { const { data } = await api.patch(`/projects/${id}`, body); setProjects((p) => p.map((x) => (x.id === id ? data : x))); if (active?.id === id) setActive(data); }
    catch { toast.error("Update failed"); }
  };

  const addUpdate = async () => {
    if (!update.trim()) return;
    await patch(active.id, { add_update: update });
    setUpdate(""); toast.success("Update posted");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-extrabold">Projects</h1><p className="text-muted-foreground">Customer projects & progress.</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-primary rounded-sm font-bold" data-testid="add-project-btn"><Plus className="h-4 w-4 mr-1" /> New Project</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading text-2xl">Create Project</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="font-semibold">Customer Name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="mt-1.5 rounded-sm" data-testid="project-cust-name" /></div>
                <div><Label className="font-semibold">Customer Email</Label><Input value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="mt-1.5 rounded-sm" data-testid="project-cust-email" /></div>
              </div>
              <div><Label className="font-semibold">Project Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Driveway Replacement" className="mt-1.5 rounded-sm" data-testid="project-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="font-semibold">Service</Label>
                  <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}><SelectTrigger className="mt-1.5 rounded-sm" data-testid="project-service"><SelectValue /></SelectTrigger>
                    <SelectContent>{SERVICES.map((s) => <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="font-semibold">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5 rounded-sm" data-testid="project-address" /></div>
              </div>
              <p className="text-xs text-muted-foreground">The customer signs in with this email (Google or password) to see this project.</p>
              <Button onClick={create} disabled={saving} className="w-full bg-primary rounded-sm font-bold" data-testid="project-create">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Project"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {projects.length === 0 ? (
        <div className="bg-card border border-border p-12 text-center text-muted-foreground"><FolderKanban className="h-8 w-8 mx-auto mb-2 opacity-50" />No projects yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {projects.map((p) => (
            <div key={p.id} className="bg-card border border-border p-5" data-testid={`project-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div><h3 className="font-heading font-bold text-lg">{p.title}</h3><p className="text-sm text-muted-foreground">{p.customer_name} · {p.customer_email}</p></div>
                <Select value={p.status} onValueChange={(v) => patch(p.id, { status: v })}>
                  <SelectTrigger className="w-36 h-8 rounded-sm text-xs" data-testid={`project-status-${p.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">Progress</span><span className="font-bold">{p.progress}%</span></div>
                <Progress value={p.progress} className="h-2" />
                <input type="range" min="0" max="100" step="10" value={p.progress} onChange={(e) => patch(p.id, { progress: Number(e.target.value) })} className="w-full mt-2 accent-primary" data-testid={`project-progress-${p.id}`} />
              </div>
              <Button onClick={() => setActive(p)} variant="outline" size="sm" className="mt-3 rounded-sm font-bold w-full" data-testid={`project-manage-${p.id}`}>Manage Updates ({p.updates?.length || 0})</Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-xl">{active.title}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Textarea value={update} onChange={(e) => setUpdate(e.target.value)} placeholder="Post an update for the customer…" rows={2} className="rounded-sm" data-testid="update-text" />
                  <Button onClick={addUpdate} className="bg-primary rounded-sm font-bold shrink-0" data-testid="post-update">Post</Button>
                </div>
                <div className="space-y-2">
                  {(active.updates || []).slice().reverse().map((u, i) => (
                    <div key={i} className="bg-muted p-3 text-sm"><p>{u.text}</p><p className="text-xs text-muted-foreground mt-1">{new Date(u.date).toLocaleString()} · {u.by}</p></div>
                  ))}
                  {(!active.updates || active.updates.length === 0) && <p className="text-sm text-muted-foreground text-center py-4">No updates yet.</p>}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
